import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { SEO } from '../../utils/seo';

export default function TrainingPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Training & Certification",
    "provider": {
      "@type": "Organization",
      "name": "LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS"
    },
    "areaServed": {
      "@type": "Place",
      "name": "United Kingdom"
    },
    "description": "We deliver a combination of in-house training, specialist instruction and white-label courses in partnership with accredited providers. This includes online, workplace-based and on-site delivery nationally."
  };

  const trainingTypes = [
    {
      icon: 'ri-computer-line',
      title: 'Online Learning',
      description: 'Accredited online courses delivered through approved training partners, accessible nationwide with flexible scheduling'
    },
    {
      icon: 'ri-building-line',
      title: 'Workplace-Based Training',
      description: 'On-site training delivered at your premises, tailored to your operational environment and specific requirements'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Classroom Instruction',
      description: 'Professional classroom-based training delivered by qualified instructors at approved training venues'
    },
    {
      icon: 'ri-team-line',
      title: 'Specialist Instruction',
      description: 'Expert-led training in specialized areas including fire safety, first aid, security operations and compliance'
    }
  ];

  const trainingAreas = [
    {
      category: 'First Aid & Emergency Response',
      courses: [
        'Emergency First Aid at Work',
        'First Aid at Work (3-day)',
        'Paediatric First Aid',
        'Mental Health First Aid',
        'AED & CPR Training'
      ],
      icon: 'ri-heart-pulse-line'
    },
    {
      category: 'Fire Safety & Prevention',
      courses: [
        'Fire Safety Awareness',
        'Fire Warden Training',
        'Fire Risk Assessment',
        'Fire Extinguisher Training',
        'Emergency Evacuation Procedures'
      ],
      icon: 'ri-fire-line'
    },
    {
      category: 'Security & Response',
      courses: [
        'Conflict Management',
        'Physical Intervention',
        'Door Supervision (SIA)',
        'CCTV Operations',
        'Incident Response'
      ],
      icon: 'ri-shield-user-line'
    },
    {
      category: 'Health, Safety & Compliance',
      courses: [
        'Health & Safety Awareness',
        'Risk Assessment Training',
        'Manual Handling',
        'Working at Height',
        'COSHH Awareness'
      ],
      icon: 'ri-shield-check-line'
    },
    {
      category: 'Safeguarding & Wellbeing',
      courses: [
        'Safeguarding Adults',
        'Safeguarding Children',
        'Prevent Duty Awareness',
        'Lone Working Safety',
        'Vulnerability & Support'
      ],
      icon: 'ri-user-heart-line'
    },
    {
      category: 'Specialist & Technical',
      courses: [
        'Electrical Safety (18th Edition)',
        'PAT Testing',
        'Legionella Awareness',
        'Asbestos Awareness',
        'Food Safety & Hygiene'
      ],
      icon: 'ri-tools-line'
    }
  ];

  const benefits = [
    {
      icon: 'ri-award-line',
      title: 'Accredited Providers',
      description: 'All training delivered through accredited instructors and approved training partners'
    },
    {
      icon: 'ri-map-pin-2-line',
      title: 'National Reach',
      description: 'Online, workplace-based and on-site delivery available across the UK'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Flexible Scheduling',
      description: 'Training scheduled to suit your operational requirements and minimize disruption'
    },
    {
      icon: 'ri-file-text-line',
      title: 'Certification Included',
      description: 'Recognized certifications provided upon successful completion of courses'
    }
  ];

  return (
    <>
      <SEO
        title="Training & Certification - Accredited Safety Compliance Training - JL Security"
        description="JL Security delivers accredited training in first aid, fire safety, security operations and compliance through qualified instructors and approved partners. Online, workplace-based and on-site delivery across the UK."
        keywords="security training, first aid training, fire safety training, compliance training, accredited training UK, workplace training"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        <Navbar isScrolled={isScrolled} />
        
        {/* Hero Section */}
        <section className="relative pt-32 pb-24 bg-gradient-to-br from-red-600 to-red-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <h6 className="text-red-100 font-semibold text-sm uppercase tracking-wider mb-4 font-inter">Training & Certification</h6>
              <h1 className="text-5xl sm:text-6xl font-bold text-white mb-6 leading-tight font-poppins">
                Accredited Training for Safety & Compliance
              </h1>
              <p className="text-lg text-red-50 leading-relaxed font-inter mb-8">
                Equipping teams with the skills and knowledge to respond effectively to emergencies, reduce workplace risk and meet training obligations through accredited courses delivered online, on-site or in the workplace.
              </p>
              <button
                onClick={scrollToContact}
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-red-600 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer font-inter"
              >
                Enquire About Training
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
            </div>
          </div>
        </section>

        {/* Training Delivery Methods */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h6 className="text-red-600 font-semibold text-sm uppercase tracking-wider mb-4 font-inter">Delivery Methods</h6>
              <h2 className="text-4xl font-bold text-gray-900 mb-6 font-poppins">
                Flexible Training Solutions
              </h2>
              <p className="text-base text-gray-600 font-inter">
                Choose from online, workplace-based or classroom training to suit your operational needs and workforce requirements.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {trainingTypes.map((type, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-8 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className={`${type.icon} text-3xl text-red-600`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 font-poppins">{type.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed font-inter">{type.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Training Areas */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h6 className="text-red-600 font-semibold text-sm uppercase tracking-wider mb-4 font-inter">Course Categories</h6>
              <h2 className="text-4xl font-bold text-gray-900 mb-6 font-poppins">
                Training Aligned to Operational Risk
              </h2>
              <p className="text-base text-gray-600 font-inter">
                We deliver training that reflects the real risks organisations face — from first aid and fire safety to security operations and compliance — through qualified instructors and approved training partners.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {trainingAreas.map((area, index) => (
                <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${area.icon} text-2xl text-red-600`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 font-poppins">{area.category}</h3>
                  </div>
                  <ul className="space-y-3">
                    {area.courses.map((course, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <i className="ri-check-line text-red-600 mt-1 flex-shrink-0"></i>
                        <span className="text-sm text-gray-700 font-inter">{course}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h6 className="text-red-600 font-semibold text-sm uppercase tracking-wider mb-4 font-inter">Why Choose Our Training</h6>
              <h2 className="text-4xl font-bold text-gray-900 mb-6 font-poppins">
                Quality Training, Recognized Certification
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center">
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className={`${benefit.icon} text-4xl text-red-600`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 font-poppins">{benefit.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed font-inter">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Partnership Statement */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl p-10 shadow-xl border border-gray-200">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-team-line text-3xl text-red-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">
                  Our Training Network
                </h3>
                <p className="text-base text-gray-600 leading-relaxed font-inter">
                  We deliver courses through a combination of accredited instructors, awarding-body partners and specialist trainers, enabling flexible delivery nationwide — online, workplace-based or on-site — without clients being restricted to a single venue or provider.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-br from-red-600 to-red-700">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-white mb-6 font-poppins">
              Ready to Book Training for Your Team?
            </h2>
            <p className="text-lg text-red-100 mb-8 font-inter">
              Contact us to discuss your training requirements and schedule courses for your workforce.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={scrollToContact}
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-red-600 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer font-inter"
              >
                Request Training Quote
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
              <a
                href="tel:07834561015"
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white font-semibold rounded-lg border-2 border-white hover:bg-white hover:text-red-600 transition-all duration-300 whitespace-nowrap cursor-pointer font-inter"
              >
                <i className="ri-phone-line mr-2"></i>
                07834 561015
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
